import React from "react";
import { visionURL } from "../config/environment";
import Jsona from "jsona";
import JsonApiService from "../services/jsonapiService";
import { useEffect, useState } from "react";
import visionCallService from "../services/visionCallService";

const dataFormatter = new Jsona();

const locationsApiService = async () =>
  new JsonApiService("locations", {
    baseUrl: `${visionURL}/admin_console/api/administration`,
    credentials: "include"
  });

export const useLocations = () => {
  const [isLoading, setIsLoading] = useState(false);
  const getLocations = React.useCallback(
    async ({ page, sortBy, filters = {} } = {}) => {
      setIsLoading(true);
      const service = await locationsApiService();
      const response = await service
        .page(page)
        .sort(sortBy)
        .where(filters)
        .all()
        .then(response => response.json());
      setIsLoading(false);
      return {
        data: dataFormatter.deserialize(response),
        ...response.meta
      };
    },
    [locationsApiService, dataFormatter]
  );

  return {
    getLocations,
    isLoading
  };
};

const locationService = async (filter = {}, sort = "") =>
  new JsonApiService("locations", {
    baseUrl: `${visionURL}/admin_console/api/administration`,
    credentials: "include",
    filter: filter,
    sort: sort
  });

export const useLocation = (locationId, includes) => {
  const [isLoading, setIsLoading] = useState(false);
  const [location, setLocation] = useState(null);

  useEffect(() => {
    const fetchLocation = async () => {
      if (locationId) {
        setIsLoading(true);
        const service = await locationService();
        const response = await service
          .include(includes)
          .find(locationId)
          .then(response => response.json());
        setIsLoading(false);
        setLocation(dataFormatter.deserialize(response));
      }
    };
    fetchLocation();
  }, [locationId]);

  const updateLocation = async (locationId, newLocationData) => {
    if (locationId) {
      setIsLoading(true);
      const service = await locationService();
      const response = await service
        .update(
          locationId,
          visionCallService.serializeRequestBody(
            { id: locationId, ...newLocationData },
            "location"
          )
        )
        .then(response => response.json());
      setIsLoading(false);
      setLocation(dataFormatter.deserialize(response));
    }
  };

  return {
    location,
    updateLocation,
    isLoading
  };
};

export const useLocationAverages = (locationId, trainingObjectType) => {
  const [isLoading, setIsLoading] = useState(false);
  const [locationAverages, setLocationAverages] = useState(null);

  useEffect(() => {
    const fetchLocationAverages = async () => {
      if (locationId) {
        setIsLoading(true);
        const service = await locationService();
        const response = await service
          .where({ training_object_type: trainingObjectType })
          .related(locationId, "averages")
          .then(response => response.json());
        setIsLoading(false);
        setLocationAverages(response);
      }
    };
    fetchLocationAverages();
  }, [locationId, trainingObjectType]);

  return {
    locationAverages,
    isLoading
  };
};

export const useLocationEmployees = ({ location }) => {
  const [loading, setLoading] = useState(false);
  const fetchEmployees = async ({ page, size, sortBy }) => {
    setLoading(true);
    const service = await locationService();
    const response = await service
      .page({ number: page, size })
      .sort(sortBy)
      .include("position")
      .related(location, "employees")
      .then(response => response.json());

    setLoading(false);
    const employees = dataFormatter.deserialize(response);
    return { data: employees, ...response.meta };
  };

  return {
    fetchEmployees,
    loading
  };
};
